import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HelpCircle, CreditCard, Users, Gift, Globe, ShieldCheck, Scale, Wallet } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { ENLogo } from '@/components/ENLogo';

const faqs = [
  {
    category: 'General',
    icon: Users,
    questions: [
      {
        q: 'What is EntrepreNatives?',
        a: 'EntrepreNatives is a global marketplace connecting Indigenous-owned businesses with consumers who want to support them, combined with a professional website builder platform. We provide escrow protection, identity verification, and tools to help Indigenous entrepreneurs thrive.',
      },
      {
        q: 'Who can join EntrepreNatives?',
        a: 'Everyone is welcome on EntrepreNatives — we never exclude any business from joining. Consumers, supporters, website-builder clients, and vendors of all backgrounds can create an account. We do, however, maintain an Indigenous preference in our vendor promotion, search ranking, and featured placements — because lifting up Indigenous entrepreneurship is our mission. Vendors who self-identify as Indigenous may be either federally recognized or non-federally recognized, and we honor both equally.',
      },
      {
        q: 'Is EntrepreNatives only for U.S. businesses?',
        a: 'Our legal terms and payment processing currently operate under U.S. law, but we welcome Indigenous businesses from around the world and are actively working to expand globally.',
      },
      {
        q: 'Do I need a credit card to create an account?',
        a: 'No! Account creation is completely free with no credit card required. You can browse vendors, become a supporter, or even start a 14-day Website Builder trial without entering any payment information.',
      },
    ],
  },
  {
    category: 'Vendor Fees & Pricing',
    icon: CreditCard,
    questions: [
      {
        q: 'What are the platform fees?',
        a: 'Buyers pay 0% — zero platform fees. What you see on the vendor\'s page is what you pay at checkout. Vendors pay a flat 10% seller fee on completed marketplace sales (we only make money when they do). The Website Builder charges only the monthly subscription — there\'s NO extra processing fee on storefront sales. Stripe\'s standard processing (2.9% + 30¢) is deducted directly by Stripe from the vendor\'s own Connect account, the same way it works for any independent business. All fees are clearly displayed before any transaction is confirmed.',
      },
      {
        q: 'What vendor subscription plans are available?',
        a: 'Three tiers — all with the same 10% flat seller fee, no hidden charges: **Basic ($0/month)**: full vendor profile, accept bookings, secure escrow, messaging, reviews. **Better Visibility ($25/month)**: boosted search ranking, category-page features, priority support, advanced analytics. **Priority Placement ($50/month)**: front-page rotation, Elite badge, ad eligibility, dedicated account manager, early feature access. You can start on Basic for free and upgrade anytime. We also run a limited **Founding Vendor** crowdfund ($150 one-time, capped at 50 seats) that unlocks 1 full year of Priority Placement plus a permanent gold badge.',
      },
      {
        q: 'What are the Website Builder plans?',
        a: 'Standard: $30/month (or $288/year — 20% off). Elite: $50/month (or $480/year — 20% off). Both include a 14-day free trial with full Elite access and no credit card required.',
      },
      {
        q: 'How do vendor payouts work?',
        a: 'Payouts are processed via Stripe Connect. After the 10% platform fee is deducted, funds are deposited into your linked bank account within 3-5 business days. You must complete Stripe Connect onboarding and identity verification to receive payouts.',
      },
      {
        q: 'What is the Founding Vendor program?',
        a: 'A limited crowdfund for the first 50 vendors who want to co-build EntrepreNatives with us. $150 one-time unlocks: a permanent gold "Founding Vendor #X" badge on your profile, 1 full year of Priority Placement (a $600 value), 6 monthly 48-hour spotlight boosts, priority search ranking, beta access to new features, quarterly founder Zooms, and a permanent spot on our public /founding-members appreciation page. Once all 50 seats are filled the program closes forever.',
      },
    ],
  },
  {
    category: 'Escrow & Payments',
    icon: ShieldCheck,
    questions: [
      {
        q: 'How does escrow work?',
        a: 'Every marketplace transaction uses our 50/50 escrow system: 50% down payment when you book, and 50% upon verified completion. All funds are held securely by Stripe (not by EntrepreNatives) until milestones are met.',
      },
      {
        q: 'What happens after a vendor delivers?',
        a: 'After a vendor marks work as delivered, you have 7 days to review and either approve (releasing the final 50% payment) or file a dispute. If you take no action within 7 days, the payment is automatically released.',
      },
      {
        q: 'What if there\'s a dispute?',
        a: 'Funds are frozen in escrow while our Trust & Safety team mediates. Both parties submit evidence, and we aim to resolve disputes within 10 business days. See our Dispute Resolution Policy for the full process.',
      },
      {
        q: 'What is a chargeback and how does it affect me?',
        a: 'A chargeback is when a buyer disputes a charge directly with their bank instead of using our platform dispute system. We strongly encourage using our dispute process first. Excessive chargebacks can result in account review or suspension.',
      },
    ],
  },
  {
    category: 'Referral System & Reward Credits',
    icon: Gift,
    questions: [
      {
        q: 'How does the referral program work?',
        a: 'Sign up as a Supporter to get your unique promo code and referral link. Share them with potential customers and vendors. When someone completes a transaction using your promo code, you earn EntrepreNatives Reward Credits equal to 3% of the transaction value.',
      },
      {
        q: 'What are EntrepreNatives Reward Credits?',
        a: 'Reward Credits are promotional incentives — NOT cash currency. They have no direct cash value and cannot be transferred between users. They are stored in your Reward Balance and can be redeemed for platform services, merchandise, or optionally converted to cash under specific conditions.',
      },
      {
        q: 'How can I redeem my Reward Credits?',
        a: 'Three ways: (1) Platform Use — apply credits toward subscriptions, vendor tools, website builder plans, or marketplace services (min $10). (2) Merchandise — redeem for official EntrepreNatives merch from the catalog. (3) Optional Cash Redemption — available at $100+ balance with identity verification, paid as a "Promotional Reward Payout" via Stripe.',
      },
      {
        q: 'Can I transfer credits to another user?',
        a: 'No. Reward Credits are non-transferable and tied to your account. They cannot be sold, traded, or given to other users.',
      },
      {
        q: 'Do my credits expire?',
        a: 'Credits do not expire as long as your account is active and in good standing. Credits are forfeited upon permanent account termination.',
      },
      {
        q: 'What counts as referral fraud?',
        a: 'Self-referrals, fake account creation, coordinated referral schemes, and transaction manipulation are all considered fraud. Violations result in credit revocation and potential account termination.',
      },
    ],
  },
  {
    category: 'Verification & Privacy',
    icon: ShieldCheck,
    questions: [
      {
        q: 'Why do I need to verify my identity?',
        a: 'Identity verification protects everyone on the platform. It ensures that vendors are real people running legitimate businesses, and gives consumers confidence. Your documents are encrypted and only accessible to our Trust & Safety team.',
      },
      {
        q: 'Is my tribal ID required?',
        a: 'No. Tribal ID is optional and private. You can provide it for our Trust & Safety review, mark yourself as "non-federally recognized," or select "prefer not to say." Indigenous identity is complex and personal — we recognize and respect both federally recognized and non-federally recognized community members equally.',
      },
      {
        q: 'Who can see my personal information?',
        a: 'Your government ID and verification documents are only visible to our Trust & Safety team. Your public profile (name, business, location) is visible to others. We never sell or share personal data. See our Privacy Policy for full details.',
      },
    ],
  },
  {
    category: 'Website Builder',
    icon: Globe,
    questions: [
      {
        q: 'What is the EntrepreNatives Website Builder?',
        a: 'A hosted website creation platform with drag-and-drop editing, e-commerce capabilities, custom domains, SSL certificates, and mobile-responsive designs. Perfect for building your business presence online.',
      },
      {
        q: 'What\'s included in the 14-day free trial?',
        a: 'Full Elite-tier access with no credit card required. This includes unlimited products, custom domain connection, AI content tools, and all premium features. After trial expiry, your site becomes inactive but data is retained for 30 days.',
      },
      {
        q: 'Can I use my own domain?',
        a: 'Yes! Elite plan includes custom domain connection. Standard plan includes a free EntrepreNatives subdomain. Domain registration fees (if applicable) are separate.',
      },
      {
        q: 'What happens to my website if I cancel?',
        a: 'Your site stays active until the end of your billing period. After cancellation, data is retained for 30 days. You can export your content before closure. Resubscribe anytime to reactivate.',
      },
    ],
  },
  {
    category: 'Disputes, Refunds & Policies',
    icon: Scale,
    questions: [
      {
        q: 'How do I get a refund?',
        a: 'Before work begins: Full refund minus processing fees. During work: Partial refund via dispute resolution. After delivery: You must file a dispute within 7 days. Subscriptions: No partial refunds for monthly; pro-rated refund within 30 days for annual plans.',
      },
      {
        q: 'What can get me banned?',
        a: 'Immediate bans apply for: fraud, identity misrepresentation, escrow manipulation, threats of violence, and chargeback abuse. Other violations follow a four-strike system with escalating consequences.',
      },
      {
        q: 'Can I appeal a strike or ban?',
        a: 'Yes. Submit an appeal within 30 days to appeals@entreprenatives.com with your account email and explanation. Appeals are reviewed within 10 business days. One appeal per incident.',
      },
      {
        q: 'Where can I find all legal documents?',
        a: 'Visit our Legal Hub at /legal for the complete list including Terms of Service, Privacy Policy, Vendor Agreement, Buyer Agreement, Website Builder Terms, Payment Policy, Escrow Terms, Referral Program Terms, and more.',
      },
    ],
  },
  {
    category: 'Orders, Progress & Revisions',
    icon: Scale,
    questions: [
      {
        q: 'How will I know my order is being worked on?',
        a: 'Every order has a live progress bar tied to the vendor\'s milestones — Order Accepted → Deposit Received → Started → 50% Halfway → Completed → Shipped/Delivered. Vendors upload a photo at each stage (required), so you always see proof of progress. The percentage updates in real time in your dashboard and email receipts.',
      },
      {
        q: 'How many free revisions do I get?',
        a: 'Every order includes 3 free revisions. Use them to refine colors, wording, sizing, or any detail until the final product matches your vision. You can request revisions from the order progress view any time before you mark the order complete.',
      },
      {
        q: 'What happens after I use all 3 free revisions?',
        a: 'Additional revisions may be subject to an extra charge decided by the vendor, unless you arranged unlimited or extended revisions with the vendor before the order began. The platform will prompt both sides to agree on a revision fee via a custom offer inside your message thread — nothing is charged without your explicit approval.',
      },
      {
        q: 'Can I see the final product before I release final payment?',
        a: 'Yes. The "Completed" milestone requires a photo/video of the finished product, and vendors can upload final deliverables (files, images) directly to the order. Your final 50% is only released from escrow after you confirm the product meets your expectations.',
      },
    ],
  },
  {
    category: 'Merchandise Redemption',
    icon: Wallet,
    questions: [
      {
        q: 'What merchandise can I redeem with credits?',
        a: 'Official EntrepreNatives merchandise including t-shirts, hoodies, mugs, sticker packs, and tote bags. Each item has an assigned credit value. Check the Merch Catalog in your Supporter Dashboard for the full selection.',
      },
      {
        q: 'How does merchandise redemption work?',
        a: 'Browse the catalog, select an item, and confirm the redemption. Credits are deducted from your balance immediately. Your order is processed and shipped to the address on file. Redemption history is tracked in your dashboard.',
      },
      {
        q: 'Can I return merchandise redeemed with credits?',
        a: 'Merchandise redeemed with credits follows standard return policies. Defective items will be replaced. Credits are not restored for returned items unless the item was defective.',
      },
    ],
  },
];

export default function FAQPage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0E0E23] via-[#1A1A3E] to-[#0E0E23] text-white" data-testid="faq-page">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(139,92,246,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      <div className="relative z-10 max-w-4xl mx-auto px-6 pt-8 pb-16">
        <div className="mb-12"><ENLogo size="md" linkTo="/" /></div>

        <motion.div className="mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 rounded-xl bg-[#8B5CF6]/20 border border-[#8B5CF6]/20">
              <HelpCircle size={24} className="text-[#A78BFA]" />
            </div>
            <div>
              <h1 className="font-syne text-3xl font-bold" data-testid="faq-title">Frequently Asked Questions</h1>
              <p className="text-[#64748B] text-sm mt-1">Everything you need to know about EntrepreNatives</p>
            </div>
          </div>
        </motion.div>

        <div className="space-y-10">
          {faqs.map((section, sectionIndex) => {
            const SectionIcon = section.icon;
            return (
              <motion.div
                key={section.category}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.05 }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <SectionIcon size={18} className="text-[#A78BFA]" />
                  <h2 className="font-syne text-lg font-bold text-white" data-testid={`faq-category-${sectionIndex}`}>{section.category}</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                  {section.questions.map((faq, index) => (
                    <AccordionItem
                      key={index}
                      value={`${section.category}-${index}`}
                      className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl px-6 overflow-hidden"
                      data-testid={`faq-item-${sectionIndex}-${index}`}
                    >
                      <AccordionTrigger className="text-left text-white/90 font-syne text-sm hover:no-underline hover:text-[#A78BFA] py-4">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-[#9CA3AF] text-sm leading-relaxed pb-4">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          className="mt-16 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <div className="bg-[#2D2D52]/40 backdrop-blur-xl border border-white/5 rounded-2xl p-8">
            <p className="text-[#9CA3AF] mb-4">
              Still have questions?{' '}
              <a href="mailto:support@entreprenatives.com" className="text-[#A78BFA] hover:text-white transition-colors">
                Contact our support team
              </a>
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Link to="/legal" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Legal Hub</Link>
              <Link to="/trust-safety" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Trust & Safety</Link>
              <Link to="/pricing" className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/5 text-[#9CA3AF] hover:text-[#A78BFA] hover:border-[#8B5CF6]/30 transition-all">Pricing</Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
